/**
 * Client Detail display helpers.
 *
 * The header subtitle was dumping `clientClassification` (Intake v2 subtype)
 * as the type badge. The Clients list already labels from legal `type`
 * (Business / Individual). Seeded and legacy rows often have
 * `type: 'business'` with `clientClassification` defaulted to HOMEOWNER, which
 * is why a company like Austin Metro Apartment Group showed HOMEOWNER.
 *
 * Past assignment dates used `new Date(value).toLocaleDateString()` without
 * an Invalid Date guard. `new Date(undefined)` / `new Date('')` do not throw
 * — they produce an Invalid Date whose locale string is the literal
 * "Invalid Date".
 */

const DATE_UNAVAILABLE = 'Date not available';

const COMMERCIAL_CLASSIFICATION_LABELS: Record<string, string> = {
  BUILDER_PRODUCTION: 'COMMERCIAL · BUILDER PRODUCTION',
  INSTITUTIONAL: 'COMMERCIAL · INSTITUTIONAL',
  PROPERTY_MANAGEMENT: 'COMMERCIAL · PROPERTY MANAGEMENT',
  BUSINESS: 'COMMERCIAL · BUSINESS',
};

export function isBusinessClientType(type?: string | null): boolean {
  const normalized = (type || '').trim().toLowerCase();
  return normalized === 'business' || normalized === 'company';
}

/**
 * Rep-facing type badge for the Client Detail header.
 * Never labels a business/company client HOMEOWNER, even when the API
 * defaulted `clientClassification` to that value.
 */
export function formatClientDetailTypeLabel(client: {
  type?: string | null;
  clientClassification?: string | null;
}): string | null {
  const classification = (client.clientClassification || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  const isBusiness = isBusinessClientType(client.type);

  if (classification && classification !== 'HOMEOWNER') {
    return (
      COMMERCIAL_CLASSIFICATION_LABELS[classification] ||
      `COMMERCIAL · ${classification.replace(/_/g, ' ')}`
    );
  }

  if (isBusiness) return 'BUSINESS';
  if (classification === 'HOMEOWNER') return 'HOMEOWNER';
  if ((client.type || '').trim().toLowerCase() === 'individual') return 'INDIVIDUAL';
  return null;
}

export function parseDisplayDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      !trimmed ||
      trimmed === 'Invalid Date' ||
      trimmed === 'null' ||
      trimmed === 'undefined'
    ) {
      return null;
    }
    const fromString = new Date(trimmed);
    return Number.isNaN(fromString.getTime()) ? null : fromString;
  }
  return null;
}

/**
 * First parseable timestamp among assignment payload aliases.
 * System-created history rows sometimes omit `assignedAt` while still
 * carrying `createdAt` / snake_case fields.
 */
export function pickAssignmentTimestamp(assignment: {
  assignedAt?: unknown;
  assigned_at?: unknown;
  createdAt?: unknown;
  created_at?: unknown;
  assignedOn?: unknown;
} | null | undefined): Date | null {
  if (!assignment) return null;
  return (
    parseDisplayDate(assignment.assignedAt) ||
    parseDisplayDate(assignment.assigned_at) ||
    parseDisplayDate(assignment.assignedOn) ||
    parseDisplayDate(assignment.createdAt) ||
    parseDisplayDate(assignment.created_at)
  );
}

/** Relative date for activity / assignment rows. Never returns "Invalid Date". */
export function formatSafeRelativeDate(
  value: unknown,
  now: Date = new Date(),
): string {
  const date = value instanceof Date && !Number.isNaN(value.getTime())
    ? value
    : parseDisplayDate(value);
  if (!date) return DATE_UNAVAILABLE;

  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

/** Absolute locale date. Never returns "Invalid Date". */
export function formatSafeLocaleDate(value: unknown): string {
  const date = parseDisplayDate(value);
  if (!date) return DATE_UNAVAILABLE;
  return date.toLocaleDateString();
}

/**
 * Sales-rep name for Client Detail About.
 *
 * The Clients list Owner column reads `assignedUser.fullName` (from
 * `assignedUserId`). About used to look only at assignee-history role
 * `REPRESENTATIVE` plus the denormalized `salesRepresentative` string.
 * GET /clients/:id strips/omits history role fields, so About showed
 * "Not assigned" while Team Access and the list still had the rep.
 */
export function resolveClientSalesRepName(
  client: {
    assignedUser?: { fullName?: string | null } | null;
    salesRepresentative?: string | null;
  },
  historyRepName?: string | null,
): string {
  return (
    client.assignedUser?.fullName?.trim() ||
    (historyRepName ?? '').trim() ||
    (client.salesRepresentative ?? '').trim() ||
    ''
  );
}
